import { useEffect, useMemo, useState } from "react";
import Form from "../../../components/form/Form";
import { useEnsureJobColors, useHub, useJobColors, useJobSources, useJobTypes } from "../../../core/Global";
import ColorSelector from "../../../components/color/ColorSelector";
import User from "../models/User";
import { toastSuccess } from "../../../components/base/Toast";
import Icon from "../../../components/base/Icon";
import Tagify from "../../../components/tagify/Tagify";
import ToggleSwitch from "../../../components/form/ToggleSwitch";
import FlexRow from "../../../components/base/FlexRow";
import { API_ROUTES } from "../../../core/Constants";


type Props = {
    userId?: string
    model?: User
    onUpdate?: (user: User) => void
}


interface TechToggleListProps {
    label: string
    name: string
    options: { id?: string; name?: string }[]
    selectedIds?: string[]
}

function TechToggleList(props: TechToggleListProps) {

    const initial = props.selectedIds ?? []

    const [selected, selectedSet] = useState<string[]>(initial)
    const [allMode, allModeSet] = useState<boolean>(initial.length === 0)

    useEffect(() => {
        const next = props.selectedIds ?? []
        selectedSet(next)
        allModeSet(next.length === 0)
    }, [props.selectedIds?.join(",")])

    const toggleItem = (id: string) => {
        selectedSet(prev => {
            if (prev.includes(id)) return prev.filter(x => x !== id)
            return [...prev, id]
        })
    }

    const toggleAll = (on: boolean) => {
        allModeSet(on)
        if (on) selectedSet([])
        else selectedSet(props.selectedIds ?? [])
    }

    return (
        <div className="mb-7">

            <input type="hidden" name={props.name} value={allMode ? "" : selected.join(",")} />

            <FlexRow className={"mb-3" + (allMode ? "" : " pb-3 border-bottom")}>
                <ToggleSwitch raw small label={props.label} checked={allMode}
                    onChange={on => toggleAll(on)} />
            </FlexRow>

            {
                !allMode &&
                <div className="row">
                    {
                        props.options.map(o => (
                            <div key={`tech-toggle-${props.name}-${o.id}`} className="col-12 col-md-6 mb-2">
                                <ToggleSwitch raw small label={o.name}
                                    checked={!!o.id && selected.includes(o.id)}
                                    onChange={() => o.id && toggleItem(o.id)} />
                            </div>
                        ))
                    }
                </div>
            }
        </div>
    )
}


export default function AccountTechDetails(props: Props) {

    const hub = useHub()
    const [working, workingSet] = useState<boolean>(false)

    const jobTypes = useJobTypes()
    const jobSources = useJobSources()
    const jobColors = useJobColors()
    useEnsureJobColors()

    // The tech stores a palette id; the picker speaks hex. Synced when the saved colour changes or
    // the palette first arrives -- not on every palette reload, which adding a colour triggers and
    // would wipe the colour just picked.
    const [jobColor, jobColorSet] = useState<string>()
    useEffect(() => {
        jobColorSet(jobColors.find(c => c.id == props.model?.techJobColorId)?.color)
    }, [props.model?.techJobColorId, jobColors.length > 0])


    return (
        <Form id="user-techsetting-details-form" className="form"
            onSubmit={(body, callback) => {

                workingSet(true);

                hub.Put("/accounts/technicians/updatetechdetails", {
                    ...body,
                    id: props.userId
                }, res => {
                    if (res.success) {
                        toastSuccess("Technician details has been updated successfully!");
                        if (props.onUpdate) props.onUpdate(res.data);
                    }
                    workingSet(false);
                })

            }}>
            <div className="card mb-5 mb-xl-10">
                <div className="card-header border-0">
                    <div className="card-title m-0">
                        <h3 className="fw-bolder m-0">Technician Details</h3>
                    </div>
                    <div className="card-action">
                        <button type="submit" className="btn btn-sm btn-primary" disabled={working}>
                            {
                                working ?
                                    <>
                                        <Icon icon="spinner-third" spin />
                                        <span className="ms-2">Updating...</span>
                                    </>
                                    :
                                    <>
                                        Update Technician
                                    </>
                            }
                        </button>
                    </div>
                </div>

                {
                    jobTypes && jobSources &&
                    <div className="card-body border-top p-9"
                        style={{
                            maxWidth: 1000
                        }}>

                        <Tagify id="tagify-tech-groups" name="groups" label="Groups" marginBottom={5}
                            apiUrl={API_ROUTES.TagifyTechGroups} selectedIds={props.model?.techGroupIds} />

                        {/* Jobs assigned to this tech take this colour (unless one was picked by hand).
                            The same picker as a job's, so a new colour can be added to the palette here. */}
                        <div className="row align-items-center mb-1">
                            <label className="col col-12 col-md-2 fs-7">Job Color</label>
                            <div className="col col-12 col-md-6">
                                <ColorSelector id="tech-job-color" variant="field" title="Technician's Job Color"
                                    color={jobColor} onSelect={c => jobColorSet(c)} />
                                <input type="hidden" name="jobColor" value={jobColor ?? ""} />
                            </div>
                        </div>
                        {/* Lined up under the field (same 2/6 columns as the label and the select). */}
                        <div className="row mb-7">
                            <div className="col-md-2"></div>
                            <div className="col col-12 col-md-6">
                                <div className="fs-8 text-muted">
                                    <Icon icon="info-circle" className="me-1" />
                                    Jobs assigned to this technician are painted this color automatically. A color
                                    someone picked by hand on a job is kept, and changing this color doesn't repaint
                                    the jobs the technician already has.
                                </div>
                            </div>
                        </div>

                        <TechToggleList label="All Skills" name="skills"
                            options={jobTypes} selectedIds={props.model?.techJobTypes} />

                        <TechToggleList label="All Sources" name="sources"
                            options={jobSources} selectedIds={props.model?.techJobSources} />

                    </div>
                }

            </div>
        </Form>
    )
}
